import React, { useState, useRef, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import { functions } from '../../../../../services/firebase';
import { getTrialTemplates } from '../../../../../constants/memberMessageTemplates';
import { listMemberEnrollments } from '../../../../../services/enrollment/queries';
import NeoCard from '../../../../../components/Common/NeoCard';
import NeoButton from '../../../../../components/Common/NeoButton';

const PREAMBLE_RE = /^(sugest[aã]o de mensagem|com base no contexto|a melhor mensagem|mensagem sugerida|aqui est[aá]|segue a mensagem)[^\n]*:?\s*/i;
const QUOTE_RE = /[“””‘’]([\s\S]+?)[“””‘’]/;

const extractAIMessage = (content) => {
    if (!content) return content;
    const delimited = content.match(/%%MSG%%\s*([\s\S]*?)\s*%%FIM%%/i);
    if (delimited) return delimited[1].trim();
    const stripped = content.replace(PREAMBLE_RE, '').trim();
    if (QUOTE_RE.test(stripped)) return stripped.match(QUOTE_RE)[1].trim();
    const labeled = content.match(/MENSAGEM:\s*\n([\s\S]*?)(?:\nMOTIVO:|\n%%FIM%%)/i);
    if (labeled) return labeled[1].trim();
    const lines = stripped.split('\n');
    const cutAt = lines.findIndex((l, i) => i > 0 && /^(essa|esta|por que|motivo|a abordagem|indica|esta abordagem)/i.test(l.trim()));
    if (cutAt > 0) return lines.slice(0, cutAt).join('\n').trim();
    return stripped;
};

const extractAIMotivo = (content) => {
    if (!content) return null;
    const delimited = content.match(/%%FIM%%\s*([\s\S]*?)$/i);
    if (delimited) return delimited[1].trim() || null;
    const labeled = content.match(/MOTIVO:\s*\n?([\s\S]*?)$/i);
    if (labeled) return labeled[1].trim() || null;
    const lines = content.split('\n');
    const cutAt = lines.findIndex((l, i) => i > 0 && /^(essa|esta|por que|motivo|a abordagem|indica)/i.test(l.trim()));
    if (cutAt > 0) return lines.slice(cutAt).join('\n').trim() || null;
    return null;
};

const SidebarAICoachChat = ({ item, idTenant, user, tenantName }) => {
    const [chatOpen, setChatOpen] = useState(true);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [chatLoading, setChatLoading] = useState(false);
    const [offTopicCount, setOffTopicCount] = useState(0);

    const chatBottomRef = useRef(null);
    const resolvedActivityRef = useRef(null);
    const resolvedItemIdRef = useRef(null);

    // Auto scroll chat to bottom when new messages arrive
    useEffect(() => {
        if (chatBottomRef.current) {
            chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chatMessages, chatLoading]);

    // Autosuggest on task change
    useEffect(() => {
        const runAutoSuggest = async () => {
            if (!item || !idTenant || chatLoading) return;
            setChatLoading(true);
            setOffTopicCount(0);

            let activityName = resolvedActivityRef.current;
            if (item.id !== resolvedItemIdRef.current) {
                activityName = null;
                resolvedItemIdRef.current = item.id;
            }

            if (!activityName && item.description) {
                const modalMatch = item.description.match(/\*\*Modalidade:\*\*\s*(.+)/i);
                if (modalMatch) activityName = modalMatch[1].trim();
            }
            if (!activityName && item.title) {
                const titleMatch = item.title.match(/(?:Experimental|Aula):\s*(.+)/i);
                if (titleMatch) activityName = titleMatch[1].trim();
            }
            if (!activityName && item.idMember) {
                try {
                    const enrollments = await listMemberEnrollments(idTenant, item.idMember);
                    const names = [...new Set(enrollments.map(e => e.activityName).filter(Boolean))];
                    if (names.length > 0) activityName = names.join(', ');
                } catch (_) { /* não bloqueia */ }
            }
            resolvedActivityRef.current = activityName;

            const templates = getTrialTemplates(item);
            const taskContext = {
                contactReason: item.contactReason,
                memberName: item.memberName,
                academyName: tenantName || null,
                activityName: activityName || null,
                contactAttempts: item.contactAttempts ?? 0,
                lastOutcome: item.contactOutcome,
                outcomeHistory: item.outcomeHistory || [],
                taskDescription: item.description || null,
                templates: templates.map(t => ({ label: t.label, message: t.message })),
            };

            const fn = httpsCallable(functions, 'aiMessageCoach');
            const result = await fn({ 
                idTenant, 
                messages: [], 
                offTopicCount: 0, 
                isAutoSuggest: true, 
                taskContext 
            });
            const { message, offTopic } = result.data;
            setChatMessages([{ role: 'assistant', content: message, offTopic, isAutoSuggest: true }]);
        };

        runAutoSuggest()
            .catch(() => {
                setChatMessages([{ 
                    role: 'assistant', 
                    content: 'Não foi possível gerar a sugestão. Configure a integração de IA em Configurações → Integrações.', 
                    error: true 
                }]);
            })
            .finally(() => setChatLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item?.id]);

    const handleChatSend = async () => {
        const text = chatInput.trim();
        if (!text || chatLoading) return;

        const userMsg = { role: 'user', content: text };
        const history = chatMessages
            .filter(m => !m.isAutoSuggest)
            .map(m => ({ role: m.role, content: m.content }));
        const updatedMessages = [...history, userMsg];

        setChatMessages(prev => [...prev, userMsg]);
        setChatInput('');
        setChatLoading(true);

        const templates = getTrialTemplates(item);
        const taskContext = {
            contactReason: item.contactReason,
            memberName: item.memberName,
            academyName: tenantName || null,
            activityName: resolvedActivityRef.current || null,
            contactAttempts: item.contactAttempts ?? 0,
            lastOutcome: item.contactOutcome,
            outcomeHistory: item.outcomeHistory || [],
            taskDescription: item.description || null,
            templates: templates.map(t => ({ label: t.label, message: t.message })),
        };

        try {
            const fn = httpsCallable(functions, 'aiMessageCoach');
            const result = await fn({ 
                idTenant, 
                messages: updatedMessages, 
                offTopicCount, 
                isAutoSuggest: false, 
                taskContext 
            });
            const { message, offTopic } = result.data;
            if (offTopic) setOffTopicCount(c => c + 1);
            setChatMessages(prev => [...prev, { role: 'assistant', content: message, offTopic }]);
        } catch {
            setChatMessages(prev => [...prev, { 
                role: 'assistant', 
                content: 'Serviço de IA indisponível. Tente novamente.', 
                error: true 
            }]);
        } finally {
            setChatLoading(false);
        }
    };

    const isCompleted = item?.status === 'done' || item?.status === 'archived';
    if (isCompleted) return null;

    return (
        <div className="mb-4">
            <button
                className="d-flex align-items-center gap-2 w-100 border-0 bg-transparent p-0 mb-2 text-start"
                onClick={() => setChatOpen(o => !o)}
                style={{ cursor: 'pointer' }}
            >
                <h6 className="text-uppercase font-size-11 fw-bold text-brand-secondary mb-0 flex-grow-1">
                    <i className="mdi mdi-robot-outline me-1 text-accent"></i>
                    Coach de Mensagens (IA)
                </h6>
                <i className={`mdi ${chatOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'} text-muted font-size-16`}></i>
            </button>

            {chatOpen && (
                <NeoCard thinBorder={true} bodyClassName="p-0">
                    <div className="p-3 custom-scrollbar" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                        {chatMessages.length === 0 && !chatLoading && (
                            <p className="text-muted font-size-12 fst-italic mb-0 text-center py-3">
                                Analisando o contexto do aluno...
                            </p>
                        )}
                        {chatMessages.map((m, i) => (
                            <div key={i} className={`mb-3 d-flex flex-column ${m.role === 'user' ? 'align-items-end' : 'align-items-start'}`}>
                                {m.isAutoSuggest && (
                                    <p className="mb-1 font-size-10 fw-bold text-accent text-uppercase d-flex align-items-center gap-1">
                                        <i className="mdi mdi-robot-outline"></i>
                                        Sugestão baseada no contexto atual
                                    </p>
                                )}
                                <div
                                    className={`rounded-3 px-3 py-2 font-size-13 ${
                                        m.role === 'user' ? 'bg-brand-primary text-white'
                                        : m.offTopic ? 'bg-warning-subtle border border-warning text-brand-primary'
                                        : m.error ? 'bg-danger-subtle text-danger'
                                        : 'bg-light border text-brand-primary'
                                    }`}
                                    style={{ maxWidth: '90%', lineHeight: '1.6' }}
                                >
                                    {m.offTopic && (
                                        <p className="mb-1 font-size-11 fw-bold text-warning-emphasis">
                                            <i className="mdi mdi-alert-outline me-1"></i>Aviso registrado
                                        </p>
                                    )}
                                    {m.role === 'assistant' && !m.error && !m.offTopic ? (() => {
                                        const msgOnly = extractAIMessage(m.content);
                                        const motivo = extractAIMotivo(m.content);
                                        return (
                                            <>
                                                <p className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>{msgOnly}</p>
                                                {motivo && (
                                                    <p className="mb-0 font-size-11 text-muted fst-italic border-top pt-2">
                                                        <i className="mdi mdi-lightbulb-outline me-1"></i>{motivo}
                                                    </p>
                                                )}
                                            </>
                                        );
                                    })() : (
                                        <span style={{ whiteSpace: 'pre-wrap' }}>{m.content}</span>
                                    )}
                                </div>
                                {m.role === 'assistant' && !m.error && !m.offTopic && (
                                    <NeoButton
                                        variant="outline-secondary"
                                        size="sm"
                                        icon="mdi-content-copy"
                                        className="mt-1"
                                        title="Copiar mensagem"
                                        onClick={() => { 
                                            navigator.clipboard.writeText(extractAIMessage(m.content)); 
                                            toast.success("Mensagem copiada!"); 
                                        }}
                                    />
                                )}
                            </div>
                        ))}
                        {chatLoading && (
                            <div className="d-flex justify-content-start mb-2">
                                <div className="bg-light border rounded-3 px-3 py-2 font-size-13 text-muted fst-italic">
                                    <i className="mdi mdi-dots-horizontal me-1"></i>Gerando sugestão...
                                </div>
                            </div>
                        )}
                        <div ref={chatBottomRef} />
                    </div>
                    <div className="border-top p-2 d-flex gap-2 align-items-end">
                        <textarea
                            className="form-control border-0 shadow-none bg-transparent font-size-13 text-brand-primary flex-grow-1 p-1"
                            rows={2}
                            placeholder='Ex: "Ela disse que não tem dinheiro, como respondo?"'
                            value={chatInput}
                            onChange={e => setChatInput(e.target.value)}
                            onKeyDown={e => { 
                                if (e.key === 'Enter' && !e.shiftKey) { 
                                    e.preventDefault(); 
                                    handleChatSend(); 
                                } 
                            }}
                            style={{ resize: 'none' }}
                            disabled={chatLoading}
                        />
                        <NeoButton
                            variant="primary" 
                            size="sm" 
                            circle={true} 
                            icon="mdi-send-outline"
                            onClick={handleChatSend} 
                            loading={chatLoading}
                            disabled={!chatInput.trim()} 
                            title="Enviar"
                        />
                    </div>
                </NeoCard>
            )}
        </div>
    );
};

export default SidebarAICoachChat;
